import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Portal from '@mui/material/Portal';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, itemImageUrl, problemOfRequest, tokenStorage } from '../../api/client';
import { apiErrorText, type ErrorText } from '../../api/errorText';
import type {
  LibraryItem,
  LiveTvChannel,
  MediaSourceSummary,
  MediaStreamInfo,
  PlaybackDecision,
  PlaybackLimits,
  RememberedTracks,
  TrackChoice,
  TrackVariant,
} from '../../api/types';
import { useApi } from '../../api/useApi';
import { toSeconds } from '../media/playbackTime';
import { browserDeviceId, buildClientProfile, kLiveHoldSeconds } from '../../playback/clientProfile';
import { createAppTheme } from '../../theme';
import Subtitles from './Subtitles';
import PlaybackDetails from './PlaybackDetails';
import { qualityChoices, resolutionName } from './qualityChoices';
import { describeStream, formatBitrate, formatClock } from './streamText';

/// How often a running player tells the server where it is. Often enough that closing a laptop
/// loses seconds rather than minutes, rarely enough that it is not a request per frame.
const REPORT_EVERY_SECONDS = 10;

/// How often an open player says it is still there. A pause asks the server for nothing, so
/// this is the only thing between a paused film and a session the server ends for it.
const PRESENCE_EVERY_MS = 30_000;

/// How close to the end of a channel still counts as watching it live. A player never sits
/// exactly on the edge - it keeps a few seconds of buffer in front of it - so "live" has to be
/// a stretch rather than a point.
const kLiveEdgeSeconds = 12;

/// How much of a channel the server keeps behind the live edge. Its segments older than this
/// are gone, so it is also as much as is worth holding on to here.
const kLiveWindowSeconds = 5 * 60;

/// How long handing a session back may hold up the one that replaces it. The two would
/// otherwise compete for the same encoder, the same Usenet connections and - for a channel -
/// the same tuner, and the new one is the one that loses. A server that has not answered by
/// then is not going to be hurried by waiting longer.
const kStopWaitMs = 3_000;

/// How long closing waits for the last position to arrive before the page underneath asks for
/// it again. Long enough for a server on the same network, short enough that a server that is
/// gone does not hold anything up.
const kFinalReportWaitMs = 2_000;

/// After this long without a mouse or a key, the controls get out of the way.
const HIDE_CONTROLS_AFTER_MS = 3000;

/// The player covers everything, including where menus and drawers normally sit. Anything
/// belonging to it has to be told to come out on top of it.
const ABOVE_THE_PICTURE = { zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 2 };

/// What the player was opened on. A film and a channel are watched in the same window but are
/// not the same thing: one has tracks, a quality and a position worth remembering, the other is
/// whatever is being broadcast right now.
export type PlayerTarget =
  /// `mediaSourceId` names which copy to play where the entry stands for more than one.
  /// Without it the entry's own default is used, which is the only copy in the ordinary case.
  | { kind: 'item'; item: LibraryItem; startAt: number; mediaSourceId?: string }
  | { kind: 'live'; channel: LiveTvChannel };

interface PlayerOverlayProps {
  target: PlayerTarget;

  /// `saved` settles once the last position has reached the server, or once waiting for it has
  /// stopped being worth it. A page that shows how far somebody got asks for that after it has
  /// settled, not before: asked straight away, it reads the position from before they closed.
  onClose: (saved: Promise<void>) => void;
}

/// The player: the whole screen, black, with the film in it and everything else out of the way
/// until somebody moves the mouse. Track, subtitle and quality are asked of the server rather
/// than of the browser, because the server is what decides how a stream is delivered - which
/// is also why it can say what it did, in a panel anybody can open.
export default function PlayerOverlay({ target, onClose }: PlayerOverlayProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // The picture is the whole interface here and everything else is chrome laid over black, so
  // this one screen stays dark whatever the rest of the application is set to. A white menu
  // over a film is nobody's idea of a player. Only the direction is carried over.
  const { direction } = useTheme();
  const playerTheme = useMemo(() => createAppTheme(direction, 'dark'), [direction]);

  const live = target.kind === 'live';
  const item = target.kind === 'item' ? target.item : undefined;

  /// What was asked for, where anything that opens a session reads it from.
  ///
  /// A ref rather than the prop itself, because the prop is built where the player is used and
  /// is a new object on every render of the page underneath. Opening is an effect, and an
  /// effect that watches an object identity would hand the session back and open another one
  /// every time that page rendered for any reason at all - a film restarting itself because
  /// something else on the page finished loading.
  const requested = useRef(target);

  // Put in step here rather than while rendering: nothing that renders reads it, and a ref
  // written during a render is a trap for whoever reads one next. It is declared above the
  // effect that opens a session, which is what makes it already current when that one runs.
  useEffect(() => {
    requested.current = target;
  }, [target]);

  /// The translator, for what opens and attaches a stream. It is a new function whenever the
  /// language changes or another namespace finishes loading, and anything that watches it would
  /// run again with it: the session handed back and opened afresh at wherever the film was
  /// first started from. Only the words of an error depend on it, and those are read when the
  /// error happens. Kept in step the same way, and for the same reason, as the target above.
  const translate = useRef(t);

  useEffect(() => {
    translate.current = t;
  }, [t]);

  /// What actually makes this a different thing to play. The effect below watches this rather
  /// than the object it came out of.
  const targetId = target.kind === 'live'
    ? `live:${target.channel.providerId}:${target.channel.id}`
    : `item:${target.item.id}:${target.mediaSourceId ?? target.item.mediaSourceId ?? ''}`;

  /// What the library says this runs for, which is what the bar is drawn against.
  const itemDuration = item?.duration ? toSeconds(item.duration) : 0;
  const startAt = target.kind === 'item' ? target.startAt : 0;
  const title = target.kind === 'item' ? target.item.title : target.channel.name;

  // A film brings its own drawn title where a provider had one. A channel never does.
  const logo = item ? itemImageUrl(item, 'Logo') : undefined;

  const container = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const lastReported = useRef(0);
  const hideTimer = useRef<number>(undefined);

  /// What is open on the server right now, kept beside the state because giving it back has to
  /// work from a page that is already going away, where a render will never come.
  const openSession = useRef<{ playback?: string; live?: string }>({});

  /// Which attempt at opening is the current one. Two can be in flight at once - a second
  /// change of track, or an effect that runs twice - and only the last one is wanted; without
  /// this, the answer to the first arrives afterwards and its session belongs to nobody.
  const attempts = useRef(0);

  /// What is playing, whichever kind it is. A channel is decided exactly like a film, so the
  /// menus, the details panel and the quality choices all read from the same three things.
  const [delivery, setDelivery] = useState<{
    source: MediaSourceSummary;
    decision: PlaybackDecision;
    limits: PlaybackLimits;

    /// The session subtitle tracks are asked for by. Held here rather than read from the ref
    /// beside it, because what is rendered has to come from what a render may look at.
    sessionId?: string;

    /// And for a channel, whose subtitles are asked for on the channel's own route.
    liveSessionId?: string;
  }>();

  const [streamUrl, setStreamUrl] = useState<string>();
  const [error, setError] = useState<ErrorText>();
  const [opening, setOpening] = useState(true);

  /// Nothing is taken in until the stream's first picture is on screen. A press while a session
  /// is still opening asks for another one before this one has answered - two workers for one
  /// film, and whichever answers last wins. The way back stays open, so a stream that never
  /// comes does not keep anybody in front of a spinner.
  const locked = opening && !error;

  const [audioIndex, setAudioIndex] = useState<number>();
  const [subtitleIndex, setSubtitleIndex] = useState<number | null>(null);
  const [maxBitrate, setMaxBitrate] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();

  /// What this person last watched this title with. The server applies it when the session
  /// opens; this copy is what lets the menus say which entry that was.
  const remembered = useApi<RememberedTracks>(
    ['items', item?.id ?? '', 'tracks'],
    () => api.itemTracks(item!.id),
    { enabled: Boolean(item), staleTime: 5 * 60 * 1000 },
  );

  /// Whether the subtitle track has been chosen in this session. Until it has, the track the
  /// server delivered - which is the remembered one where there is one - is what is shown, and
  /// after it the person's choice stands whatever the server would have done.
  const subtitleChosen = useRef(false);

  /// Which resolution's bitrates are open. The menu is two levels: a picture size, and what it
  /// may cost.
  const [openResolution, setOpenResolution] = useState<number>();

  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(startAt);
  const [duration, setDuration] = useState(0);

  /// What can be moved around in right now. For a film that is all of it; for a channel it is
  /// what the server still has - the segments behind the live edge, which is what makes going
  /// back in time possible at all. Both ends move: the near one as the broadcast goes on, the
  /// far one as old segments fall away.
  const [timeshift, setTimeshift] = useState({ start: 0, end: 0 });

  /// Where in the item the current stream begins, in seconds.
  ///
  /// Zero for a file and for a channel. Not zero for a transcode that was asked to start
  /// somewhere else: a transcode is written forwards from where it starts, so the media
  /// element counts from nought however far into the film that is. Every time that crosses
  /// between the element and the interface passes through this - the scrubber, the clock, and
  /// the position reported to the server - and getting it wrong is a film that appears to
  /// start over every time somebody drags the bar.
  ///
  /// A ref rather than state: nothing renders from it. What renders is the position, which is
  /// already state, and holding this as state as well would set two of them for every tick.
  const streamStart = useRef(0);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [menu, setMenu] = useState<{ kind: 'audio' | 'subtitle' | 'quality'; anchor: HTMLElement }>();

  /// Where playback should pick up when the stream is opened again after a change of track.
  ///
  /// Kept up to date while a stream is really playing, and left alone the rest of the time.
  /// The element is not a witness to ask at the moment of a change: let go of, or not yet
  /// moved to where it was started, it says nought - and a second change of track made while
  /// the first is still opening would take that for where somebody was.
  const resumeAt = useRef(startAt);

  /// True while somebody is dragging the bar. The picture goes on playing underneath, and its
  /// own position would otherwise pull the handle out from under the finger holding it.
  const scrubbing = useRef(false);

  /// What is reading the playlist, where one is being read. Held rather than left to the
  /// effect's own cleanup, because a session can be handed back in the middle of a render and
  /// a player still attached to it goes on asking for segments that no longer exist.
  const playlistReader = useRef<import('hls.js').default>(undefined);

  /// Which attachment is the current one. Fetching the library takes a moment, and in that
  /// moment the session it was going to play can already be gone.
  const attachment = useRef(0);

  /// Which attachment has started playing. Until the current one has, what the element says
  /// about time belongs to nothing anybody watched: it is nought after being let go of, and
  /// wherever the library put it before the first segment is in.
  const playedIn = useRef<number>(undefined);

  /// Whether the element is showing the current stream, which is the only time its clock says
  /// where somebody is. Letting go of a stream sets that clock back to nought and announces it
  /// with a time update of its own; reported as a position, that update is a resume point
  /// thrown away, because the server forgets anything this close to the start.
  const showingStream = useCallback(
    (element: HTMLVideoElement) =>
      playedIn.current === attachment.current
      && element.readyState >= HTMLMediaElement.HAVE_METADATA
      && Number.isFinite(element.currentTime),
    [],
  );

  /// Lets go of the picture, at once. Anything that hands a session back does this first.
  const detach = useCallback(() => {
    attachment.current += 1;

    playlistReader.current?.destroy();
    playlistReader.current = undefined;

    const element = video.current;

    if (element) {
      element.removeAttribute('src');
      element.load();
    }
  }, []);

  /// Hands one session back. Everything that opens one goes through here, including the
  /// answer that arrives for a request nobody is waiting for any more.
  ///
  /// What comes back settles once the server has let go, or once `kStopWaitMs` has passed,
  /// whichever is first; it never fails. Whatever opens the next session waits for it - the old
  /// one still holds the encoder, the connections and the tuner the new one is about to ask
  /// for. Whatever is only tidying up does not.
  const giveBack = useCallback(
    (held: { playback?: string; live?: string }, options: { keepalive?: boolean } = {}): Promise<void> => {
      const stopped: Promise<void>[] = [];

      if (held.playback) {
        stopped.push(
          api.stopSession(held.playback, options).catch(() => {
            // The server ends what it stops hearing from anyway.
          }),
        );
      }

      // A tuner held open is a tuner nobody else can use.
      if (held.live) {
        stopped.push(
          api.stopLiveTv(held.live, options).catch(() => {
            // Same again.
          }),
        );
      }

      return stopped.length > 0 ? within(Promise.all(stopped), kStopWaitMs) : Promise.resolve();
    },
    [],
  );

  /// Gives back whatever this player holds, once. Called when it closes, when a change of
  /// track replaces the session, and when the page goes out from under it.
  const release = useCallback(
    (options: { keepalive?: boolean } = {}) => {
      const held = openSession.current;

      openSession.current = {};

      // Whatever is still on its way is for a player that is going. Its answer is handed
      // straight back when it arrives, the same as any other nobody is waiting for.
      attempts.current += 1;

      // The address goes with the session. Leaving it behind would let the picture be
      // attached to it a second time - which is exactly what happens when React runs an
      // effect twice - and a player pointed at a session that has been given back spends its
      // time asking for things that are not there any more.
      detach();
      setStreamUrl(undefined);

      void giveBack(held, options);
    },
    [detach, giveBack],
  );

  const open = useCallback(
    async (options: {
      audioStreamIndex?: number;
      subtitleStreamIndex?: number | null;
      maxBitrate?: number;
      maxHeight?: number;
      useOriginalQuality?: boolean;
    }) => {
      const attempt = (attempts.current += 1);

      /// The channel this request takes the place of, where the server is closing it rather
      /// than this player. Until the request has reached the server it is still this player's
      /// to give back, and it is given back here wherever the request never gets there.
      let handedOver: string | undefined;

      try {
        // A change of track or quality is a new session, and the one it replaces is finished
        // with. The picture is let go of at once: a player still attached to a session that has
        // been handed back spends its time asking for things that are not there any more.
        //
        // The session itself is given back before the next is asked for, and this waits until
        // it has gone. Asked for side by side, the two compete for one encoder, one set of
        // Usenet connections, one tuner - and a change of soundtrack on a channel comes back as
        // "no tuner free", taken by the very channel it was changing.
        //
        // A channel that takes the place of a channel does not wait for that at all: the
        // request names the one it replaces, and the server closes it and lets go of its tuner
        // before tuning the next. That is one round trip rather than two, and it cannot be
        // outrun - the tuner is free by the time it is asked for, however slow the stop was.
        // Stopping it here as well would only be a second stop for something already gone.
        //
        // The address is not cleared here, though release() would: the answer to this request
        // replaces it anyway, and clearing state on the way into an effect is how a render
        // loop starts.
        const asked = requested.current;
        const held = openSession.current;

        handedOver = asked.kind === 'live' ? held.live : undefined;

        openSession.current = {};
        detach();
        await giveBack(handedOver ? { playback: held.playback } : held);

        // Asked again while the old one was going. The later request is the one that opens,
        // and it has nothing of this one's to give back - so what this one was going to have
        // the server close is given back the ordinary way.
        if (attempt !== attempts.current) {
          if (handedOver) {
            void giveBack({ live: handedOver });
          }

          return;
        }

        // The server is asked first, whichever kind this is, and nothing here is set until it
        // has answered. A channel has no tracks to pick and no quality to choose: it is tuned,
        // and what the tuner sends is what there is.
        const opened: {
          live?: Awaited<ReturnType<typeof api.startLiveTv>>;
          media?: Awaited<ReturnType<typeof api.startPlayback>>;
        } =
          asked.kind === 'live'
            ? {
                // A channel takes the same choices a film does: which track, and how much
                // picture. What it cannot take is a position - it is on now or it is not.
                live: await api.startLiveTv(asked.channel.id, await buildClientProfile(browserDeviceId()), {
                  providerId: asked.channel.providerId,
                  audioStreamIndex: options.audioStreamIndex,
                  maxBitrate: options.useOriginalQuality ? undefined : options.maxBitrate,
                  maxHeight: options.useOriginalQuality ? undefined : options.maxHeight,
                  replaces: handedOver,
                }),
              }
            : {
                media: await api.startPlayback(
                  asked.mediaSourceId ?? asked.item.mediaSourceId!,
                  await buildClientProfile(browserDeviceId()),
                  { ...options, startPositionTicks: Math.round(resumeAt.current * 10_000_000) },
                ),
              };

        // Somebody asked again while this was on its way. What came back is a session nobody
        // is going to watch, so it goes straight back rather than being left to time out.
        if (attempt !== attempts.current) {
          void giveBack({ playback: opened.media?.session.id, live: opened.live?.session.id });

          return;
        }

        // A media element cannot set headers on the URL it plays, which is why both delivery
        // routes also accept the token this way.
        const token = tokenStorage.read();
        const query = token ? `?token=${encodeURIComponent(token)}` : '';

        if (opened.live) {
          openSession.current = { live: opened.live.session.id };
          streamStart.current = 0;

          if (opened.live.session.source && opened.live.session.decision) {
            setDelivery({
              source: opened.live.session.source,
              decision: opened.live.session.decision,
              limits: opened.live.session.limits,
              liveSessionId: opened.live.session.id,
            });

            setMaxHeight(opened.live.session.limits.maxHeight ?? undefined);
            setMaxBitrate(opened.live.session.limits.maxBitrate ?? undefined);
          }

          setStreamUrl(`${opened.live.streamUrl}${query}`);
        } else if (opened.media) {
          openSession.current = { playback: opened.media.session.id };

          // A transcode is written forwards from wherever it was asked to start, so its own
          // timeline begins at nought however far into the film that is. Everything the media
          // element says about time is relative to that, and this is what puts it back.
          streamStart.current = opened.media.startPositionSeconds ?? 0;
          setDelivery({
            source: opened.media.session.source,
            decision: opened.media.session.decision,
            limits: opened.media.session.limits,
            sessionId: opened.media.session.id,
          });

          // The server may have applied the user's own default for this network. Taking the
          // ceilings from the answer rather than from what was asked is what makes the menu
          // show the quality that is really running.
          setMaxHeight(opened.media.session.limits.maxHeight ?? undefined);
          setMaxBitrate(opened.media.session.limits.maxBitrate ?? undefined);

          // A subtitle the server delivered without being asked is the remembered one. Showing
          // it is the whole point of remembering it - the server can only deliver the track,
          // and drawing it over the picture is this player's half of that.
          if (!subtitleChosen.current) {
            setSubtitleIndex(deliveredSubtitle(opened.media.session.decision) ?? null);
          }

          setStreamUrl(`${opened.media.streamUrl}${query}`);
        }
      } catch (caught) {
        // Whether the server got as far as closing it is not something a failure says. A stop
        // for a session that is already gone costs a "not found" and nothing else; a tuner
        // left held costs the next channel.
        if (handedOver) {
          void giveBack({ live: handedOver });
        }

        if (attempt !== attempts.current) {
          return;
        }

        setError(apiErrorText(caught, translate.current));
        setStreamUrl(undefined);

        // Only on the way out with nothing to show. A session that opened keeps the spinner
        // until its first picture is on screen - the server answering is not the film playing,
        // and a transcode an hour into a film read out of Usenet can take a while to get there.
        // Only the attempt anybody is still waiting for, too: an older one failing would
        // otherwise take the spinner off a stream that is still being opened.
        setOpening(false);
      }
    },
    [detach, giveBack],
  );

  useEffect(() => {
    // Opening a session is the point of this component; it is what it synchronises with -
    // which is what there is to play, not the object it was handed in.
    void open({});
  }, [open, targetId]);

  useEffect(() => {
    const element = video.current;

    if (!element || !streamUrl) {
      return undefined;
    }

    // A file plays as it is.
    if (!streamUrl.includes('.m3u8')) {
      element.src = streamUrl;

      return undefined;
    }

    const generation = (attachment.current += 1);
    const current = () => attachment.current === generation;

    // A playlist takes a library everywhere except on Apple's own players, and asking the
    // browser whether it can play one is no help: Chrome answers "maybe" and then cannot. So
    // the library decides, and the browser's own HLS is what is left when it cannot be used.
    void (async () => {
      const { default: Hls } = await import('hls.js');

      if (!current()) {
        return;
      }

      if (!Hls.isSupported()) {
        if (element.canPlayType('application/vnd.apple.mpegurl') !== '') {
          element.src = streamUrl;
        } else {
          setError({ message: translate.current('player.hlsUnsupported') });
        }

        return;
      }

      const player = new Hls({
        enableWorker: true,

        // Where somebody stopped, from the first request on. Left to itself the library reads
        // segment nought first and is only moved on once the metadata is in - and a film's
        // playlist names every segment, so each of those requests moves the worker: back to the
        // beginning, then forward again to where it had been started in the first place.
        startPosition: !live && resumeAt.current > streamStart.current ? resumeAt.current - streamStart.current : -1,

        // How much of a channel to keep behind the picture. This is what somebody rewinds
        // into, so it is worth the memory - but no more of it than the server itself still
        // has: anything older is a moment nobody can go back to anyway, and a television's
        // media buffer is small enough that holding it ends in the browser refusing new
        // segments. A film needs far less, because the server still has the whole of it.
        backBufferLength: live ? kLiveWindowSeconds : 90,

        // A head start counted in time rather than in segments: a count means whatever the
        // segment length makes it. The same number the server was told, so it hands over the
        // playlist once there is exactly this much behind the edge - see kLiveHoldSeconds.
        liveSyncDuration: kLiveHoldSeconds,

        // How far behind the edge somebody may fall before being put back on it: exactly as
        // far as the server keeps. Anything nearer is timeshift somebody chose and is left
        // alone; anything further is a moment whose segments are already gone.
        liveMaxLatencyDuration: kLiveWindowSeconds,

        // And when it does drift behind, five percent faster until it has caught up. The
        // alternative is a jump, and a jump is something a viewer sees.
        maxLiveSyncPlaybackRate: 1.05,

        // How long a segment may take, which is not the same question for the two.
        //
        // A film's segment is held by the server until the worker has written it, and where
        // the worker first has to be moved there - a seek, or a player that asked ahead of
        // where it is - that is how long the move takes: a second on a disk, routinely more
        // than ten seconds for a film read out of Usenet, and up to thirty before the server
        // gives up itself. A request abandoned in the meantime is not free. Its retry asks for
        // the same segment again, and the server does real work for every one of them. So a
        // film waits longer than the server does, and retries a timeout once rather than
        // making it worse.
        //
        // A broadcast's segments are written as it goes out and come fast, or something is
        // wrong. One that breaks up is still a broadcast to carry on with: the defaults give
        // up after a handful of tries, and a satellite feed deserves more patience than that.
        fragLoadPolicy: {
          default: live
            ? {
                maxTimeToFirstByteMs: 15_000,
                maxLoadTimeMs: 30_000,
                timeoutRetry: { maxNumRetry: 4, retryDelayMs: 500, maxRetryDelayMs: 4_000 },
                errorRetry: { maxNumRetry: 8, retryDelayMs: 500, maxRetryDelayMs: 8_000 },
              }
            : {
                maxTimeToFirstByteMs: 45_000,
                maxLoadTimeMs: 90_000,
                timeoutRetry: { maxNumRetry: 1, retryDelayMs: 1_000, maxRetryDelayMs: 4_000 },
                errorRetry: { maxNumRetry: 6, retryDelayMs: 1_000, maxRetryDelayMs: 8_000 },
              },
        },

        // A version's playlist does not exist until its first segment is whole, and the server
        // holds the request until it does rather than answering "not found" for something that
        // is on its way. How long that takes is how long the encoder needs to reach the point
        // somebody asked for - a second for a file on a disk, tens of seconds for a film being
        // read out of Usenet an hour in. The default gives up after ten, which turns a seek
        // that was working into a network error.
        playlistLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 45_000,
            maxLoadTimeMs: 60_000,
            timeoutRetry: { maxNumRetry: 2, retryDelayMs: 1_000, maxRetryDelayMs: 4_000 },
            errorRetry: { maxNumRetry: 4, retryDelayMs: 1_000, maxRetryDelayMs: 8_000 },
          },
        },
        manifestLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 45_000,
            maxLoadTimeMs: 60_000,
            timeoutRetry: { maxNumRetry: 2, retryDelayMs: 1_000, maxRetryDelayMs: 4_000 },
            errorRetry: { maxNumRetry: 4, retryDelayMs: 1_000, maxRetryDelayMs: 8_000 },
          },
        },
      });

      player.on(Hls.Events.ERROR, (_event, data) => {
        if (!current()) {
          return;
        }

        // What the server said, where it said something: the library hands over the request it
        // failed on, and the server's answer to it names what went wrong.
        const problem = problemOfRequest(data.networkDetails);

        // A channel whose stream failed is over, and nothing the library tries will bring it
        // back: the server has already closed the session and given the tuner back. Said at
        // once, with the worker's own reason beside it, rather than after a round of retries
        // that each come back "not found".
        if (problem?.code === 'livetv.stream_failed') {
          detach();
          setError({ ...apiErrorText(problem, translate.current), detail: problem.message });

          return;
        }

        // Otherwise only the ones it cannot come back from: the library retries the rest by
        // itself - a segment the server is still making among them, which it answers with
        // "ask again" rather than with a failure.
        if (data.fatal) {
          setError(
            problem
              ? apiErrorText(problem, translate.current)
              : { message: data.error?.message ?? translate.current('player.streamFailed') },
          );
        }
      });

      player.loadSource(streamUrl);
      player.attachMedia(element);

      playlistReader.current = player;
    })();

    return () => {
      if (current()) {
        detach();
      }
    };
  }, [detach, live, streamUrl]);

  /// Tells the server where the player is. Throttled while it runs, and always on a pause or
  /// an end - those are the moments somebody actually leaves.
  ///
  /// Only from a stream that is really playing. Between streams - a change of track, a jump
  /// the server has to move the worker for, a player closed before its first picture - the
  /// element's clock says nought, and the server takes nought as "start again from the
  /// beginning". Saying nothing then leaves the last position that was true where it is.
  ///
  /// What comes back settles when the server has the position, or has failed to take it. It
  /// never fails itself.
  const report = useCallback(
    (force = false): Promise<void> => {
      const element = video.current;

      // Nobody keeps a position in live television.
      if (!item || !element || !showingStream(element)) {
        return Promise.resolve();
      }

      const at = streamStart.current + element.currentTime;

      if (!force && Math.abs(at - lastReported.current) < REPORT_EVERY_SECONDS) {
        return Promise.resolve();
      }

      lastReported.current = at;

      return api
        .reportProgress(
          item!.id,
          at,

          // The element only knows how long its own stream is, which for a transcode that
          // started halfway is the rest of the film. The item's own length is the truth.
          itemDuration || (Number.isFinite(element.duration) ? element.duration : undefined),
        )
        .then(
          () => undefined,
          () => {
            // Losing a position is not worth interrupting somebody watching a film.
          },
        );
    },
    [item, itemDuration, showingStream],
  );

  /// Remembers what somebody just picked, so the next episode - and the next evening - comes up
  /// the way they watch this title.
  ///
  /// What is sent is the language of the track, never its index: the same programme is a
  /// different file every episode, and its third stream is not the same thing twice. What is not
  /// named here is left as it was, so choosing a subtitle says nothing about the soundtrack.
  const remember = useCallback(
    (choice: Partial<TrackChoice>) => {
      if (!item) {
        return;
      }

      const current = remembered.data;
      const next: TrackChoice = {
        audioLanguage: choice.audioLanguage !== undefined ? choice.audioLanguage : (current?.audioLanguage ?? null),
        audioVariant: choice.audioVariant ?? current?.audioVariant ?? 'Main',
        subtitleLanguage:
          choice.subtitleLanguage !== undefined ? choice.subtitleLanguage : (current?.subtitleLanguage ?? null),
        subtitleVariant: choice.subtitleVariant ?? current?.subtitleVariant ?? 'Main',
        subtitlesOff: choice.subtitlesOff !== undefined ? choice.subtitlesOff : (current?.subtitlesOff ?? false),
      };

      // Shown at once rather than when the server answers, so the mark moves with the check
      // and never sits on two entries for a moment.
      queryClient.setQueryData<RememberedTracks>(['items', item.id, 'tracks'], (old) =>
        old ? { ...old, ...next } : old,
      );

      void api
        .saveItemTracks(item.id, next)
        .then((saved) => queryClient.setQueryData(['items', item.id, 'tracks'], saved))
        .catch(() => {
          // Worth remembering, not worth interrupting a film for. The choice still holds for as
          // long as this player is open; only the next one starts over.
        });
    },
    [item, queryClient, remembered.data],
  );

  const close = useCallback(() => {
    // Sent before the picture is let go of, which would set its clock back to nought. The page
    // underneath is not kept waiting for it to arrive - it is handed the wait instead, to ask
    // for the position again once the server has it.
    const saved = within(report(true), kFinalReportWaitMs);

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }

    release();
    onClose(saved);
  }, [onClose, release, report]);

  useEffect(() => {
    // Somebody who paused is still here. Without this the server sees a client that stopped
    // asking for anything, which is exactly what a closed window looks like.
    const timer = window.setInterval(() => {
      const id = openSession.current.playback;

      if (id) {
        void api.reportPresence(id).catch(() => {
          // A missed one costs nothing; the next is in half a minute.
        });
      }
    }, PRESENCE_EVERY_MS);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    // The two ways a player stops existing without pressing anything: the page goes, or this
    // component is taken off the screen. Both give back what is open, and `release` only ever
    // acts once, so a close that already did it is not undone here.
    const onPageHide = () => release({ keepalive: true });

    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      release({ keepalive: true });
    };
  }, [release]);

  /// Anything that counts as "somebody is here" puts the controls back and starts the clock.
  const wake = useCallback(() => {
    setControlsVisible(true);

    window.clearTimeout(hideTimer.current);

    hideTimer.current = window.setTimeout(() => {
      if (video.current && !video.current.paused) {
        setControlsVisible(false);
      }
    }, HIDE_CONTROLS_AFTER_MS);
  }, []);

  useEffect(() => {
    // The controls start visible, so this only has to start the clock that hides them.
    const timer = window.setTimeout(() => {
      if (video.current && !video.current.paused) {
        setControlsVisible(false);
      }
    }, HIDE_CONTROLS_AFTER_MS);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  const toggle = useCallback(() => {
    const element = video.current;

    if (!element) {
      return;
    }

    if (element.paused) {
      void element.play();
    } else {
      element.pause();
    }
  }, []);

  /// Opening the session again, with something about it changed: another track, another
  /// quality, or another moment to start from. Which of those it is, is the server's business
  /// to act on and not the browser's to work around.
  const reopenWith = useCallback(
    (options: {
      audioStreamIndex?: number;
      subtitleStreamIndex?: number | null;
      maxBitrate?: number;
      maxHeight?: number;
      /// Set when the change is "back to the original", where undefined means "leave it alone".
      clearQuality?: boolean;
      /// Where to start. Without it, playback carries on from where it is now.
      at?: number;
    }) => {
      // Where the film is, which is what the time updates have been writing down all along -
      // and what they stopped writing down the moment the picture was last let go of.
      resumeAt.current = options.at ?? resumeAt.current;
      setMenu(undefined);
      setOpening(true);
      setError(undefined);

      void open({
        audioStreamIndex: options.audioStreamIndex ?? audioIndex,
        subtitleStreamIndex: options.subtitleStreamIndex !== undefined ? options.subtitleStreamIndex : subtitleIndex,
        maxBitrate: options.clearQuality ? undefined : (options.maxBitrate ?? maxBitrate),
        maxHeight: options.clearQuality ? undefined : (options.maxHeight ?? maxHeight),

        // "Original" is a choice, not the absence of one: without saying so, the server would
        // apply this person's own default for the network they are on.
        useOriginalQuality: options.clearQuality === true,
      });
    },
    [audioIndex, maxBitrate, maxHeight, open, subtitleIndex],
  );

  /// Moves the running stream to another moment, rather than opening a new one.
  ///
  /// Nothing about what this browser can play changed because somebody dragged the bar, so
  /// nothing is decided again and nothing is measured again: the server keeps the session and
  /// its decision and starts only the worker at the new moment. Where there is no session to
  /// move - it ended, or it was never a transcode - it is opened again the old way.
  const moveTo = useCallback(
    async (at: number) => {
      const sessionId = openSession.current.playback;

      if (!sessionId) {
        reopenWith({ at });

        return;
      }

      const attempt = (attempts.current += 1);

      resumeAt.current = at;
      setMenu(undefined);
      setOpening(true);
      setError(undefined);

      // The picture is let go of before the server is asked. The playlist address does not
      // change, and a player still attached to it would read the new worker's segments into
      // the old stream's timeline.
      detach();

      try {
        const moved = await api.seekPlayback(sessionId, Math.round(at * 10_000_000));

        // Dragged again while this was on its way: the later request is the one that counts,
        // and it has already moved the stream on.
        if (attempt !== attempts.current) {
          return;
        }

        streamStart.current = moved.startPositionSeconds ?? at;

        const token = tokenStorage.read();
        const query = new URLSearchParams();

        if (token) {
          query.set('token', token);
        }

        // Which worker this is. The address is otherwise the one already playing, and the
        // player would not attach to something it thinks it already has.
        query.set('worker', moved.session.transcodeSessionId ?? String(attempt));

        // The spinner stays until the new worker's first picture is on screen.
        setStreamUrl(`${moved.streamUrl}?${query.toString()}`);
      } catch {
        if (attempt !== attempts.current) {
          return;
        }

        // The session could not be moved - it ended under the player, or the server found
        // nothing to move. Opening it again at that moment is still right, and it reports its
        // own failure if that fails too.
        reopenWith({ at });
      }
    },
    [detach, reopenWith],
  );

  /// How far into the item the current stream reaches. For a file that is all of it; for a
  /// transcode it is where it started plus however much has been encoded, which grows while
  /// somebody watches.
  const seekableEnd = () => {
    const element = video.current;

    if (!element) {
      return 0;
    }

    const encoded = element.seekable.length > 0
      ? element.seekable.end(element.seekable.length - 1)
      : Number.isFinite(element.duration) ? element.duration : 0;

    return streamStart.current + encoded;
  };

  const seekTo = useCallback(
    (seconds: number) => {
      const element = video.current;

      if (!element) {
        return;
      }

      // A channel has no zero to count from: the earliest moment is wherever the window now
      // begins, and the latest is the live edge.
      const floor = live ? timeshift.start : 0;
      const ceiling = live ? timeshift.end : duration > 0 ? duration : seconds;

      const target = Math.min(Math.max(seconds, floor), ceiling > 0 ? ceiling : seconds);

      // Inside what this stream holds: the element can do it by itself. Its own clock starts
      // where the stream does, so the film's time has to come back out of it.
      if (live || (target >= streamStart.current && target <= seekableEnd())) {
        element.currentTime = live ? target : target - streamStart.current;
        setPosition(target);

        // Written down at once rather than left to the next time update, which only writes
        // once this stream has played: a film the browser would not start by itself can still
        // be moved about in before anybody presses play.
        if (!live) {
          resumeAt.current = target;
        }

        return;
      }

      // Outside it - past what has been encoded, or back before this stream began. There is
      // nothing there to seek into, so the server moves the stream: same session, same
      // decision, a worker started again at that moment.
      setPosition(target);
      void moveTo(target);
    },
    [duration, live, timeshift.end, timeshift.start, moveTo],
  );

  const seekBy = useCallback(
    (seconds: number) => {
      const element = video.current;

      if (!element) {
        return;
      }

      // Where the film is, not where the stream is: a transcode opened an hour in counts from
      // nought, and ten seconds back off its own clock is the start of the film. While the
      // next stream is still opening the element's clock is no help at all, and where the film
      // is, is where that stream was asked to start.
      const from = live || showingStream(element)
        ? streamStart.current + element.currentTime
        : resumeAt.current;

      seekTo(from + seconds);
    },
    [live, seekTo, showingStream],
  );

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void container.current?.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setFullscreen(Boolean(document.fullscreenElement));

    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  /// The keys every player has had for twenty years.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      wake();

      if (locked && event.key !== 'Escape') {
        return;
      }

      switch (event.key) {
        case ' ':
        case 'k':
          event.preventDefault();
          toggle();
          break;
        case 'ArrowLeft':
          seekBy(-10);
          break;
        case 'ArrowRight':
          seekBy(10);
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'm':
          setMuted((value) => !value);
          break;
        case 'Escape':
          if (!document.fullscreenElement) {
            close();
          }

          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [close, locked, seekBy, toggle, toggleFullscreen, wake]);

  /// How far back in the broadcast somebody is. Zero at the live edge, growing as they rewind.
  const behindLive = Math.max(timeshift.end - position, 0);

  const audioStreams = (delivery?.source.streams ?? []).filter((stream) => stream.kind === 'Audio');
  const subtitleStreams = (delivery?.source.streams ?? []).filter((stream) => stream.kind === 'Subtitle');

  /// Which soundtrack is actually playing: the one picked here, else the one the server chose -
  /// which is where a remembered language ends up - and only failing both, the file's default.
  const playingAudio =
    audioIndex
    ?? delivery?.decision.streams.find((plan) => plan.kind === 'Audio')?.sourceStreamIndex
    ?? (audioStreams.find((stream) => stream.isDefault) ?? audioStreams[0])?.index;

  const chosenSubtitle = subtitleStreams.find((candidate) => candidate.index === subtitleIndex);

  /// The one entry in each menu that carries the mark. A remembered choice is a language and a
  /// kind of track, and a file can still have two of those - the same German film in DTS and in
  /// AC-3 - so the mark goes on the one playing where that is one of them, and otherwise on the
  /// one the server would pick first.
  const rememberedAudio = rememberedTrack(
    audioStreams,
    remembered.data?.audioLanguage,
    remembered.data?.audioVariant,
    playingAudio,
  );
  const rememberedSubtitle = remembered.data?.subtitlesOff
    ? undefined
    : rememberedTrack(
        subtitleStreams,
        remembered.data?.subtitleLanguage,
        remembered.data?.subtitleVariant,
        subtitleIndex ?? undefined,
      );

  /// Where the chosen subtitle track is asked for. A channel's are on the channel's own route
  /// and are always drawn as it plays; a film's words are asked for as WebVTT first, and the
  /// server says when they are in the index instead. Whether an index is finished is the
  /// server's to say rather than something guessed from which route it came down.
  const subtitleSource = (() => {
    if (subtitleIndex === null) {
      return undefined;
    }

    if (live) {
      return delivery?.liveSessionId === undefined
        ? undefined
        : { base: `/api/livetv/sessions/${delivery.liveSessionId}/subtitles/${subtitleIndex}`, textFirst: false };
    }

    return delivery?.sessionId === undefined
      ? undefined
      : {
          base: `/api/playback/sessions/${delivery.sessionId}/subtitles/${subtitleIndex}`,
          textFirst: chosenSubtitle?.isTextBased !== false,
        };
  })();

  // Only what the file can actually give. Offering 4K for a 360p source is an invitation to
  // upscale, which costs an encoder and gives back nothing that was not already there.
  const sourceVideo = (delivery?.source.streams ?? []).find((stream) => stream.kind === 'Video');
  const sourceHeight = sourceVideo?.height;
  const sourceBitrate = delivery?.source.bitrate ?? sourceVideo?.bitrate ?? undefined;

  const choices = qualityChoices(sourceHeight, sourceBitrate);

  const screen = (
    <Box
      ref={container}
      onMouseMove={wake}
      onClick={(event) => {
        wake();

        // Only a click on the picture itself. The controls sit in their own boxes on top of
        // it, and a click that lands on one of those is meant for that control.
        if (!locked && (event.target === event.currentTarget || (event.target as HTMLElement).tagName === 'VIDEO')) {
          toggle();
        }
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: 'black',
        display: 'grid',
        placeItems: 'center',
        cursor: controlsVisible ? 'default' : 'none',

        // Everything over a picture is white. Anything that takes its colour from what it
        // sits in - an icon, a piece of text in a plain box - would otherwise take it from
        // the page underneath, and half of them would be black ink on a night scene.
        color: 'common.white',
      }}
    >
      {streamUrl && (
        <Box
          component="video"
          ref={video}
          autoPlay
          // Without this an iPhone takes the picture into its own full screen the moment it
          // plays, with its own controls over everything this player draws - the subtitles it
          // lays over the film included. The picture stays in the page, and the page stays in
          // charge of it.
          playsInline
          controls={false}
          onLoadedMetadata={() => {
            const element = video.current;

            if (element) {
              // The film's own length where it is known. A transcode's playlist only knows
              // what has been encoded so far, and a bar that grows while somebody watches is
              // not a bar anybody can aim at.
              setDuration(itemDuration || (Number.isFinite(element.duration) ? element.duration : 0));

              // Where somebody stopped is where they start again, and where a change of track
              // picks up. A film's playlist names the whole film whatever the worker is doing,
              // so this is a seek like any other: the server moves the worker to the segment
              // that is asked for.
              // Where hls.js was given the position already, it is there, and seeking to it again
              // only asks for the same segment twice. The browser's own HLS is told by the
              // playlist itself where to start, where the server wrote that in, and is left
              // alone the same way; where it was not told, it starts at nought and is moved here.
              const resumeFrom = resumeAt.current - streamStart.current;

              if (
                resumeAt.current > streamStart.current &&
                resumeAt.current <= seekableEnd() &&
                Math.abs(element.currentTime - resumeFrom) > 1
              ) {
                element.currentTime = resumeFrom;
              }
            }
          }}
          onTimeUpdate={() => {
            const element = video.current;

            if (!element) {
              return;
            }

            // Where the film is, written down for as long as it can be known - which is only
            // while the current stream plays. The update that letting go of a stream sends,
            // with its clock back at nought, is not somewhere anybody was.
            const showing = showingStream(element);

            if (showing && !live) {
              resumeAt.current = streamStart.current + element.currentTime;
            }

            // Not while somebody is dragging the bar: the handle belongs to them until they
            // let go of it. Between two streams it stays where the next one starts, rather than
            // falling back to the beginning for as long as that one takes to open.
            if (!scrubbing.current) {
              setPosition(showing || live ? streamStart.current + element.currentTime : resumeAt.current);
            }

            if (element.seekable.length > 0) {
              setTimeshift({
                start: element.seekable.start(0),
                end: element.seekable.end(element.seekable.length - 1),
              });
            }

            report();
          }}
          onPlay={() => {
            setPlaying(true);
            wake();
          }}
          // The first picture of this stream, and the moment the spinner has been waiting for.
          // From here on the element's clock is where somebody is.
          onPlaying={() => {
            playedIn.current = attachment.current;
            setOpening(false);
          }}
          // Enough is in to play, and nothing is playing: the browser would not start a film
          // with sound on its own, and it waits for the play button instead. The spinner
          // would otherwise cover that button's invitation for good.
          onCanPlayThrough={() => {
            if (video.current?.paused) {
              setOpening(false);
            }
          }}
          onPause={() => {
            setPlaying(false);
            setControlsVisible(true);
            report(true);
          }}
          onEnded={() => {
            // A film that ends is over. A channel that ends stopped sending, which is worth
            // saying rather than closing the window on somebody.
            if (live) {
              setError({ message: t('livetv.ended') });

              return;
            }

            close();
          }}
          muted={muted}
          // A layer of its own over the black rather than a box the screen has to size: laid
          // out by the grid, a percentage height is measured against a row the picture itself
          // sizes, so its aspect ratio grows the box past the screen and cuts the film's own
          // edges off. Against the fixed screen the box is the screen, and contain fits the
          // whole frame inside it - letterboxed or pillarboxed, never cropped.
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'black',
          }}
        />
      )}

      {/* Subtitles ride beside the picture rather than inside it, so turning them on is never a
          re-encode. Keyed on the track: a new choice starts from nothing rather than showing the
          last one's cues. */}
      {subtitleSource && (
        <Subtitles
          key={subtitleSource.base}
          base={subtitleSource.base}
          textFirst={subtitleSource.textFirst}
          video={video}
          streamStart={streamStart}
        />
      )}

      {/* Not over an error: a stream that failed is not one that is still coming. */}
      {opening && !error && <CircularProgress sx={{ position: 'absolute' }} aria-label={t('common.loading')} />}

      {error && (
        <Alert severity="error" sx={{ maxWidth: 520, position: 'absolute' }}>
          <AlertTitle>{t('item.cannotPlay')}</AlertTitle>
          <div>{error.message}</div>
          {error.remedy && <div>{error.remedy}</div>}
          {/* The server's own words, which are English whatever the page is in - marked as such,
              so a screen reader reads them as English and a right-to-left page sets them the
              way they were written. */}
          {error.detail && (
            <Typography variant="caption" component="div" lang="en" dir="auto" sx={{ mt: 1, opacity: 0.8 }}>
              {error.detail}
            </Typography>
          )}
        </Alert>
      )}

      {/* The way back and the title, over the picture and gone with the controls. */}
      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          insetInline: 0,
          top: 0,
          // The same side air the controls at the foot of the screen keep, so the way back sits
          // over the play button rather than a step nearer the edge on a wide screen.
          px: { xs: 2, md: 4 },
          py: 2,
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, transparent 100%)',
          opacity: controlsVisible ? 1 : 0,
          transition: 'opacity 200ms ease',
          pointerEvents: controlsVisible ? 'auto' : 'none',
        }}
      >
        {/* Leading the title rather than trailing it, so it sits in the same place whether the
            title is written or drawn. Back, not away: what is behind the player is the page the
            film was started from. The arrow points the way the language came from. */}
        <IconButton onClick={close} aria-label={t('common.back')} sx={{ color: 'common.white' }}>
          {direction === 'rtl' ? <ArrowForwardIcon /> : <ArrowBackIcon />}
        </IconButton>

        {logo ? (
          <Box
            component="img"
            src={logo}
            alt={title}
            sx={{
              minWidth: 0,
              maxWidth: { xs: 180, sm: 280 },
              maxHeight: { xs: 32, sm: 44 },
              objectFit: 'contain',
              display: 'block',
              filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.7))',
            }}
          />
        ) : (
          <Typography variant="h3" sx={{ minWidth: 0 }} noWrap>
            {title}
          </Typography>
        )}
      </Stack>

      {/* Inert rather than a disabled prop on every control: nothing in it can be pressed,
          dragged or reached with the tab key until the picture is there. */}
      <Stack
        spacing={1}
        inert={locked}
        aria-busy={locked}
        sx={{
          position: 'absolute',
          insetInline: 0,
          bottom: 0,
          px: { xs: 2, md: 4 },
          pb: 2,
          pt: 6,
          background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, transparent 100%)',
          opacity: controlsVisible ? (locked ? 0.5 : 1) : 0,
          transition: 'opacity 200ms ease',
          pointerEvents: controlsVisible && !locked ? 'auto' : 'none',
        }}
      >
        {/* Nothing to seek in: what is on is on. */}
        <Slider
          value={position}
          min={live ? timeshift.start : 0}
          max={live ? Math.max(timeshift.end, timeshift.start + 1) : duration || 1}
          step={1}
          // While the handle is moving, only the handle moves. Seeking happens once, when it
          // is let go of - a drag across a transcode would otherwise be one restart per pixel.
          onChange={(_, value) => {
            if (typeof value === 'number') {
              scrubbing.current = true;
              setPosition(value);
            }
          }}
          onChangeCommitted={(_, value) => {
            scrubbing.current = false;

            if (typeof value === 'number') {
              seekTo(value);
            }
          }}
          aria-label={t('player.seek')}
          sx={{ '& .MuiSlider-thumb': { width: 14, height: 14 } }}
        />

        {/* On a phone there is not room for a row of this on one line, so it wraps rather than
            pushing the last buttons off the edge where nothing can reach them. */}
        <Stack
          direction="row"
          useFlexGap
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: { xs: 0.25, sm: 1 },
            rowGap: 0.5,
          }}
        >
          <IconButton onClick={toggle} aria-label={playing ? t('player.pause') : t('player.play')}>
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          <IconButton onClick={() => setMuted((value) => !value)} aria-label={t('player.mute')}>
            {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: { xs: 'auto', sm: 108 }, whiteSpace: 'nowrap' }}
          >
            {live
              ? behindLive < kLiveEdgeSeconds
                ? t('livetv.live')
                : t('livetv.behindLive', { time: formatTime(behindLive) })
              : `${formatTime(position)} / ${formatTime(duration)}`}
          </Typography>

          {live && behindLive >= kLiveEdgeSeconds && (
            <Tooltip title={t('livetv.backToLive')}>
              <Button size="small" color="error" variant="contained" onClick={() => seekTo(timeshift.end)}>
                {t('livetv.live')}
              </Button>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {audioStreams.length > 1 && (
            <Tooltip title={t('player.audio')}>
              <IconButton
                onClick={(event) => setMenu({ kind: 'audio', anchor: event.currentTarget })}
                aria-label={t('player.audio')}
              >
                <GraphicEqIcon />
              </IconButton>
            </Tooltip>
          )}

          {subtitleStreams.length > 0 && (
            <Tooltip title={t('player.subtitles')}>
              <IconButton
                onClick={(event) => setMenu({ kind: 'subtitle', anchor: event.currentTarget })}
                aria-label={t('player.subtitles')}
              >
                <SubtitlesIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={t('player.quality')}>
              <IconButton
                onClick={(event) => setMenu({ kind: 'quality', anchor: event.currentTarget })}
                aria-label={t('player.quality')}
              >
              <HighQualityIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('player.details')}>
            <IconButton
              onClick={() => setDetailsOpen(true)}
              aria-label={t('player.details')}
              color={detailsOpen ? 'primary' : 'default'}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>

          {/* Not on a phone. The player already covers the screen there, so the button offers
              to do what has been done - and on an iPhone it could not do it anyway, because
              Safari has no full screen to give a page. */}
          <Tooltip title={t('player.fullscreen')}>
            <IconButton
              onClick={toggleFullscreen}
              aria-label={t('player.fullscreen')}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Menu
        open={menu?.kind === 'audio'}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(undefined)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={ABOVE_THE_PICTURE}
      >
        {audioStreams.map((stream) => (
          <MenuItem
            key={stream.index}
            selected={playingAudio === stream.index}
            onClick={() => {
              setAudioIndex(stream.index);

              // A track nobody wrote a language on cannot be remembered as one, and picking it
              // says the last language is not what they want either - so it is forgotten.
              remember({ audioLanguage: stream.language, audioVariant: variantOf(stream) });
              reopenWith({ audioStreamIndex: stream.index });
            }}
          >
            <ListItemIcon>{playingAudio === stream.index && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText
              primary={trackLabel(stream)}
              secondary={[
                ...trackRoles(stream, t),
                stream.index === rememberedAudio ? t('player.remembered') : undefined,
              ]
                .filter(Boolean)
                .join(' · ') || undefined}
            />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        open={menu?.kind === 'subtitle'}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(undefined)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={ABOVE_THE_PICTURE}
      >
        <MenuItem
          selected={subtitleIndex === null}
          onClick={() => {
            subtitleChosen.current = true;
            setSubtitleIndex(null);
            remember({ subtitleLanguage: null, subtitlesOff: true });
            setMenu(undefined);
          }}
        >
          <ListItemIcon>{subtitleIndex === null && <CheckIcon fontSize="small" />}</ListItemIcon>
          <ListItemText
            primary={t('player.none')}
            secondary={remembered.data?.subtitlesOff ? t('player.remembered') : undefined}
          />
        </MenuItem>

        {subtitleStreams.map((stream) => (
          <MenuItem
            key={stream.index}
            selected={subtitleIndex === stream.index}
            onClick={() => {
              // No new session: the picture is untouched and only the track beside it changes.
              subtitleChosen.current = true;
              setSubtitleIndex(stream.index);
              remember({ subtitleLanguage: stream.language, subtitleVariant: variantOf(stream), subtitlesOff: false });
              setMenu(undefined);
            }}
          >
            <ListItemIcon>{subtitleIndex === stream.index && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText
              primary={trackLabel(stream)}
              secondary={[
                // A forced track carries the lines a viewer needs whatever language they
                // speak - the alien dialogue, the sign on the door - and picking it by
                // mistake instead of the full one is an evening of nearly no subtitles.
                stream.isForced ? t('player.forced') : undefined,
                ...trackRoles(stream, t),
                stream.isTextBased === false ? t('player.subtitlesArePictures') : undefined,
                stream.index === rememberedSubtitle ? t('player.remembered') : undefined,
              ]
                .filter(Boolean)
                .join(' · ') || undefined}
            />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        open={menu?.kind === 'quality'}
        anchorEl={menu?.anchor}
        onClose={() => {
          setMenu(undefined);
          setOpenResolution(undefined);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={ABOVE_THE_PICTURE}
      >
        <MenuItem
          selected={maxHeight === undefined && maxBitrate === undefined}
          onClick={() => {
            setMaxHeight(undefined);
            setMaxBitrate(undefined);
            setOpenResolution(undefined);
            reopenWith({ clearQuality: true });
          }}
        >
          <ListItemIcon>
            {maxHeight === undefined && maxBitrate === undefined && <CheckIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary={t('player.original')} secondary={describeSource(delivery?.source)} />
        </MenuItem>

        <Divider />

        {choices.map(({ height, bitrates, hasAutomatic }) => (
          <Box key={height}>
            <MenuItem
              selected={maxHeight === height}
              onClick={() => {
                // A size with nothing left to choose underneath is picked in one click.
                if (bitrates.length > 0) {
                  setOpenResolution((open) => (open === height ? undefined : height));

                  return;
                }

                setMaxHeight(height);
                setMaxBitrate(undefined);
                reopenWith({ maxHeight: height, maxBitrate: undefined });
              }}
            >
              <ListItemIcon>{maxHeight === height && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={`${height}p`} secondary={resolutionName(height)} />
              {bitrates.length > 0 && (openResolution === height ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </MenuItem>

            {/* The bitrate is a detail of the resolution, so it lives under it rather than
                beside it: nobody picks eight megabits without first picking a picture size. */}
            <Collapse in={openResolution === height} unmountOnExit>
              {hasAutomatic && (
                <MenuItem
                  selected={maxHeight === height && maxBitrate === undefined}
                  onClick={() => {
                    setMaxHeight(height);
                    setMaxBitrate(undefined);
                    reopenWith({ maxHeight: height, maxBitrate: undefined, clearQuality: false });
                  }}
                  sx={{ paddingInlineStart: 5 }}
                >
                  <ListItemIcon>
                    {maxHeight === height && maxBitrate === undefined && <CheckIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={t('player.automatic')} />
                </MenuItem>
              )}

              {bitrates.map((bitrate) => (
                <MenuItem
                  key={bitrate}
                  selected={maxHeight === height && maxBitrate === bitrate}
                  onClick={() => {
                    setMaxHeight(height);
                    setMaxBitrate(bitrate);
                    reopenWith({ maxHeight: height, maxBitrate: bitrate });
                  }}
                  sx={{ paddingInlineStart: 5 }}
                >
                  <ListItemIcon>
                    {maxHeight === height && maxBitrate === bitrate && <CheckIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={formatBitrate(bitrate)} />
                </MenuItem>
              ))}
            </Collapse>
          </Box>
        ))}
      </Menu>

      <Drawer
        // A drawer's anchor is already logical: MUI turns "right" into the left edge where the
        // page reads right to left, so this is the trailing edge in every language.
        anchor="right"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        sx={ABOVE_THE_PICTURE}
      >
        {delivery && (
          <PlaybackDetails
            decision={delivery.decision}
            source={delivery.source}
            liveSessionId={delivery.liveSessionId}
            onClose={() => setDetailsOpen(false)}
          />
        )}
      </Drawer>
    </Box>
  );

  // Hung on the document rather than on the page that opened it. A player covers the screen,
  // and anything above it in the page - a hero image with a wash over it is enough - becomes
  // what "the screen" means to a fixed position, leaving a strip of the page showing along
  // the top.
  return (
    <Portal>
      <ThemeProvider theme={playerTheme}>{screen}</ThemeProvider>
    </Portal>
  );
}

/// The subtitle the server chose for this session - the one asked for, or the remembered one.
///
/// Whatever its action says. A browser names only WebVTT, so any other track is marked discard:
/// nothing the stream carries has it. That is true and beside the point, because this player
/// never takes subtitles out of the stream - it asks for every track on its own routes, the
/// same way it does for one picked from the menu. Reading discard as "none" left a remembered
/// SubRip or PGS track named in the details and missing from the picture.
function deliveredSubtitle(decision: PlaybackDecision): number | undefined {
  return decision.streams.find((stream) => stream.kind === 'Subtitle')?.sourceStreamIndex;
}

/// Whether a track is in the language somebody chose for this title.
///
/// Compared as the two were written, because both come from the same kind of place: what is
/// stored is the tag a stream carried, and what is shown is the tag a stream carries. Two files
/// that spell one language differently - "ger" in one and "de" in the next - simply do not mark
/// it, which is a missing mark rather than a wrong one.
function speaks(stream: MediaStreamInfo, language: string | null | undefined): boolean {
  return Boolean(language) && stream.language?.toLowerCase() === language!.toLowerCase();
}

/// What kind of track of its language this is, named the way the server remembers it. A track
/// flagged twice counts as the narrower of the two, as it does on the server.
function variantOf(stream: MediaStreamInfo): TrackVariant {
  if (stream.isAudioDescription) {
    return 'AudioDescription';
  }

  if (stream.isCommentary) {
    return 'Commentary';
  }

  if (stream.isForced) {
    return 'Forced';
  }

  return stream.isHearingImpaired ? 'HearingImpaired' : 'Main';
}

/// Which single track a remembered choice stands for in this file: those of its language and
/// kind - or of its language and the film itself, where this file has none of that kind - and
/// of those the one playing, else the first.
function rememberedTrack(
  streams: MediaStreamInfo[],
  language: string | null | undefined,
  variant: TrackVariant | undefined,
  playing: number | undefined,
): number | undefined {
  const spoken = streams.filter((stream) => speaks(stream, language));
  const ofKind = (wanted: TrackVariant) => spoken.filter((stream) => variantOf(stream) === wanted);
  const kind = ofKind(variant ?? 'Main');
  const main = ofKind('Main');
  const candidates = kind.length > 0 ? kind : main.length > 0 ? main : spoken;

  return (candidates.find((stream) => stream.index === playing) ?? candidates[0])?.index;
}

/// A track as a menu names it: what somebody called it, where anybody did, and then what it is
/// - written by the same helpers the details panel writes streams with, so one file's
/// soundtrack reads the same wherever it is shown. A subtitle's own title is already the last
/// thing `describeStream` says about it, so only a soundtrack's is put in front.
function trackLabel(stream: MediaStreamInfo): string {
  return stream.kind === 'Audio' && stream.title
    ? `${stream.title} · ${describeStream(stream)}`
    : describeStream(stream);
}

/// What a track is for, as the container marks it: the words that tell a German audio
/// description from the German soundtrack beside it, which have the same language, the same
/// codec and often the same channels. A title somebody wrote usually says it already.
function trackRoles(stream: MediaStreamInfo, t: TFunction): string[] {
  return [
    stream.isAudioDescription ? t('player.audioDescription') : undefined,
    stream.isCommentary ? t('player.commentary') : undefined,
    stream.isHearingImpaired ? t('player.hearingImpaired') : undefined,
    stream.isOriginal ? t('player.originalLanguage') : undefined,
  ].filter((role): role is string => role !== undefined);
}

/// What "original" means for this file, so the entry says something rather than nothing.
function describeSource(source: MediaSourceSummary | undefined): string | undefined {
  const video = source?.streams.find((stream) => stream.kind === 'Video');

  return video?.height ? `${video.height}p` : undefined;
}

/// What `promise` settles to, or nothing once `ms` have passed - whichever comes first. For
/// waits worth having but not worth being held up by: a server that is slow to answer is a
/// server the person in front of the screen should not have to wait for.
function within(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, ms);

    void promise.then(
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      () => {
        window.clearTimeout(timer);
        resolve();
      },
    );
  });
}

/// A clock for the controls. An element that has not loaded yet has no time to show, and NaN
/// on the bar is worse than a zero.
function formatTime(seconds: number): string {
  return Number.isFinite(seconds) ? formatClock(seconds) : '0:00';
}
